<template>
  <service-wrapper
    :title="$t('DEALS.LEXIS_NEXIS.SERVICE_NAME')"
    :subtitle="$t('DEALS.DEAL_PROGRESS.UNDERWRITING.BUSINESS_CREDIT')"
    img="/images/lexisnexis.svg"
    :is-success="haveSuccess"
    :date="serviceInfo.date"
    :json-data="serviceInfo.data?.[selectedScoreType]"
    :service-type="selectedScoreType"
    :service-name="$t('DEALS.DEAL_PROGRESS.UNDERWRITING.BUSINESS_CREDIT')"
    :update-service-json-payload="updateServiceJsonPayload"
    :provider-name="PROVIDER_LEXISNEXIS_BUSINESS_CREDIT"
  >
    <template v-if="canRunService">
      <primary-button
        v-show="!hasStartedService && !showContent"
        @click="showContent = true"
      >
        {{ $t("COMMON.RUN_SERVICE") }}
      </primary-button>
    </template>
    <icon-button
      v-if="
        isJsonTreeShown(PROVIDER_LEXISNEXIS_BUSINESS_CREDIT) &&
        !!serviceInfo.data?.[selectedScoreType]
      "
      :icon="IconCurlyBraces"
      view-box="0 -3 17 24"
      :data-cy="PROVIDER_LEXISNEXIS_BUSINESS_CREDIT"
      :class="{ active: isJsonTreeShown(PROVIDER_LEXISNEXIS_BUSINESS_CREDIT) }"
      @click.stop="
        serviceJsonTreeStateStore.updateServiceJsonStatus(
          PROVIDER_LEXISNEXIS_BUSINESS_CREDIT
        )
      "
    />
    <template #content>
      <div class="w-full space-y-5">
        <div
          v-if="
            !serviceInfo.data?.[selectedScoreType] &&
            !serviceHasUnexpectedStatus(serviceStatus)
          "
        >
          {{ $t("DEALS.SERVICES_DESCRIPTION.LEXISNEXIS") }}
        </div>
        <service-error
          v-if="serviceHasUnexpectedStatus(serviceStatus)"
          :have-response="
            !isEmpty(serviceInfo.data?.[selectedScoreType]) ||
            !isEmpty(serviceStatus)
          "
          :service-error-message="serviceStatus"
        />
        <template v-if="hasStartedService || showContent || hasScoreBeenRun">
          <div
            class="flex w-full items-center justify-between"
            data-cy="checkmark-options"
          >
            <div>
              <lf-h3>
                {{
                  $t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SMALL_BUSINESS_TITLE")
                }}
              </lf-h3>
            </div>
            <div class="flex justify-end">
              <checkmark-tab
                v-for="option in scoreOptions"
                :key="option.id"
                class="mx-3 cursor-pointer"
                :data-cy="option.id"
                :active="selectedScoreType === option.id"
                @click="selectedScoreType = option.id"
              >
                <template #prefix>
                  <icon-base
                    :icon="IconCheckCircle"
                    class="mr-2"
                    :class="
                      hasSuccessfulRuns(option.id)
                        ? 'text-primary'
                        : 'text-gray-400'
                    "
                  />
                </template>
                {{ option.name }}
              </checkmark-tab>
              <confirm-service-start
                :date="serviceInfo.date"
                :download-data="serviceInfo?.data?.[selectedScoreType] || null"
                :confirm-handler="startService"
                :status="serviceStatus"
                :service-name="
                  $t('DEALS.DEAL_PROGRESS.UNDERWRITING.BUSINESS_CREDIT')
                "
                :provider-name="PROVIDER_LEXISNEXIS_BUSINESS_CREDIT"
                :has-json-view="!isEmpty(serviceInfo.data?.[selectedScoreType])"
                :service-type="selectedScoreType"
                :is-loading="loading"
              >
                <template
                  v-if="serviceInfo?.data?.[selectedScoreType]"
                  #expand-all
                >
                  <div>
                    <span
                      v-tooltip="{ content: tooltipText }"
                      class="flex min-h-full min-w-full cursor-pointer items-center justify-center"
                      @click="toggleAllSectionsExpanded"
                    >
                      <icon-button
                        view-box="0 -4 15 24"
                        :icon="
                          allSectionsExpanded ? 'expandall' : 'collapseall'
                        "
                      />
                    </span>
                  </div>
                </template>
              </confirm-service-start>
            </div>
          </div>
          <submitted-data
            v-if="serviceInfo.requestData?.[selectedScoreType]"
            :request-data="
              serviceInfo.requestData[selectedScoreType] as SearchBy
            "
            data-cy="submitted-data"
          />
          <div v-if="serviceInfo.data?.[selectedScoreType]">
            <lexis-nexis-business-credit-report
              ref="BusinessCreditReportRef"
              :data="
                serviceInfo.data[selectedScoreType]
                  .SmallBusinessBipCombinedReportResponseEx
              "
            />
          </div>
          <div
            v-else-if="!hasUnexpectedStatus && hasStartedService"
            class="flex items-center justify-center"
          >
            <span>
              {{ serviceStatus }}
            </span>
          </div>
        </template>
      </div>
    </template>
  </service-wrapper>
</template>
<script lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { getAsyncComponent, usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useServices } from "@/hooks/services";
import { useJsonTreeStateStore } from "@/stores/serviceJsonViewer";

import type { UpdateServiceJsonPayload } from "@/models/applications";
import type { ScoreType } from "@/models/commercialData/lexisnexis/LexisNexisSmallBusinessBlendedCreditScore";
import type { SearchBy } from "@/models/commercialData/lexisnexis/LexisNexisSmallBusinessBlendedCreditScoreRequestData";
</script>
<script setup lang="ts">
import isEmpty from "lodash/isEmpty";

import {
  COMMERCIAL_DATA_STATUS_NOT_STARTED,
  COMMERCIAL_DATA_STATUS_SUCCESS,
  PROVIDER_LEXISNEXIS_BUSINESS_CREDIT,
} from "@/helpers/constants";
import { serviceHasUnexpectedStatus } from "@/helpers/services";

import ServiceWrapper from "@/components/deals/dataServices/ServiceWrapper.vue";
import LexisNexisBusinessCreditReport from "@/components/deals/dataServices/businessCredit/lexisNexis/LexisNexisBusinessCreditReport.vue";
import SubmittedData from "@/components/deals/dataServices/businessCredit/lexisNexis/LexisNexisBusinessCreditSubmittedData.vue";
import IconCheckCircle from "@/components/icons/IconCheckCircle.vue";
import IconCurlyBraces from "@/components/icons/IconCurlyBraces.vue";
import CheckmarkTab from "@/components/ui/LfCheckmarkTab.vue";
import IconButton from "@/components/ui/buttons/IconButton.vue";
import ConfirmServiceStart from "@/views/businessCreditDeals/components/ConfirmServiceStart.vue";

const ServiceError = getAsyncComponent(
  () => import("@/components/deals/dataServices/ServiceError.vue")
);

const { dispatch } = useStore();
const { activeDeal } = useDeals();
const serviceJsonTreeStateStore = useJsonTreeStateStore();
const { canRunService } = useServices();
const { t } = useI18n();

const { isJsonTreeShown } = storeToRefs(useJsonTreeStateStore());

const scoreOptions = [
  {
    id: "credit_score",
    name: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.CREDIT_SCORE"),
  },
  {
    id: "blended_score",
    name: t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.BLENDED_SCORE"),
  },
] as const;

const showContent = ref(false);
const allSectionsExpanded = ref(true);
const selectedScoreType = ref<ScoreType>("credit_score");
const BusinessCreditReportRef = ref<InstanceType<
  typeof LexisNexisBusinessCreditReport
> | null>(null);

const { loading, fetchWrapper: startService } = usePromiseWrapper(async () =>
  dispatch("applications/enrichApp", {
    provider: PROVIDER_LEXISNEXIS_BUSINESS_CREDIT,
    options: {
      scoreType: selectedScoreType.value,
    },
  })
);

const updateServiceJsonPayload = computed<
  Omit<UpdateServiceJsonPayload, "content">
>(() => ({
  service_id: PROVIDER_LEXISNEXIS_BUSINESS_CREDIT,
  options: {
    scoreType: selectedScoreType.value,
  },
}));

const serviceStatus = computed(
  () =>
    (selectedScoreType.value === "blended_score"
      ? serviceInfo.value.blended_status
      : serviceInfo.value.credit_status) ?? "-"
);

const tooltipText = computed(() =>
  allSectionsExpanded.value
    ? t("COMMON.COLLAPSE_TABLES_TOOLTIP")
    : t("COMMON.EXPAND_TABLES_TOOLTIP")
);

const serviceInfo = computed(() => ({
  blended_status:
    activeDeal.value.commercialData?.statuses?.lexis_nexis
      ?.small_business_credit_score?.blended_score || "",
  credit_status:
    activeDeal.value.commercialData?.statuses?.lexis_nexis
      ?.small_business_credit_score?.credit_score || "",
  date:
    (selectedScoreType.value === "credit_score"
      ? activeDeal.value.commercialData?.dates
          ?.lexis_nexis_small_business_credit_score
      : activeDeal.value.commercialData?.dates
          ?.lexis_nexis_small_business_blended_credit_score) || "",
  requestData: {
    credit_score:
      activeDeal.value.commercialData?.request_data?.lexis_nexis
        ?.small_business_credit_score?.credit_score
        ?.SmallBusinessBipCombinedReportRequest?.SearchBy || null,
    blended_score:
      activeDeal.value.commercialData?.request_data?.lexis_nexis
        ?.small_business_credit_score?.blended_score
        ?.SmallBusinessBipCombinedReportRequest?.SearchBy || null,
  },
  data:
    activeDeal.value.commercialData?.lexis_nexis?.small_business_credit_score ||
    null,
}));

const haveSuccess = computed(() =>
  [serviceInfo.value.credit_status, serviceInfo.value.blended_status].includes(
    COMMERCIAL_DATA_STATUS_SUCCESS
  )
);

const hasStartedService = computed(() => {
  const status =
    selectedScoreType.value === "blended_score"
      ? serviceInfo.value.blended_status
      : serviceInfo.value.credit_status;

  return status !== COMMERCIAL_DATA_STATUS_NOT_STARTED;
});

const hasScoreBeenRun = computed(() => {
  return Object.values(
    activeDeal.value.commercialData?.statuses?.lexis_nexis
      ?.small_business_credit_score || {}
  ).some((status) => status === COMMERCIAL_DATA_STATUS_SUCCESS);
});

const hasUnexpectedStatus = computed(() =>
  selectedScoreType.value === "blended_score"
    ? serviceHasUnexpectedStatus(serviceInfo.value.blended_status)
    : serviceHasUnexpectedStatus(serviceInfo.value.credit_status)
);

const hasSuccessfulRuns = (id: ScoreType) => {
  const statuses =
    activeDeal.value.commercialData?.statuses?.lexis_nexis
      ?.small_business_credit_score;

  return statuses && statuses[id] === COMMERCIAL_DATA_STATUS_SUCCESS;
};

const toggleAllSectionsExpanded = () => {
  allSectionsExpanded.value = !allSectionsExpanded.value;
  BusinessCreditReportRef.value?.toggleAllSections(allSectionsExpanded.value);
};

watch(
  () => serviceInfo.value.data,
  (newValue) => {
    if (isEmpty(newValue)) {
      return;
    }
    showContent.value = true;
    selectedScoreType.value =
      isEmpty(newValue["credit_score"]) && !isEmpty(newValue["blended_score"])
        ? "blended_score"
        : "credit_score";
  },
  { immediate: true }
);
</script>
